import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { contractRequestAPI } from '@/lib/api';

/**
 * DeleteRequestDialog — permanently deletes a contract request and
 * every workflow row that belongs to it. Previously terminal requests
 * were soft-archived, but the user wants Delete to actually delete —
 * so this dialog now uses `hardDelete` (force=true) unconditionally.
 * Admins who still want to preserve history can use the Archive
 * button on the row directly.
 */
export const DeleteRequestDialog = ({ open, onOpenChange, request, onDeleted }) => {
  const [pending, setPending] = React.useState(false);

  const handleConfirm = async () => {
    if (!request) return;
    setPending(true);
    try {
      // Force delete even if the request is in a terminal state
      // (Approved / Declined / Terminated). The backend accepts
      // `force=true` to bypass the archive-on-terminal safeguard.
      await contractRequestAPI.hardDelete(request.request_id);
      toast.success('Request permanently deleted');
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-request-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Request</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mb-3">
              Carrier: <strong>{request?.carrier_name}</strong><br />
              Status: <strong>{request?.current_status?.replace(/_/g, ' ')}</strong>
            </span>
            <span className="text-red-600">
              This request and every workflow history row that belongs to it will be <strong>permanently deleted</strong>. This action cannot be undone.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className="bg-red-500 hover:bg-red-600"
            data-testid="confirm-delete-request-btn"
          >
            <Trash2 size={14} className="mr-1" /> Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteRequestDialog;
